'use client';

import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { ToolPartCustomRendererArgs } from '@/features/copilot/ui/Tool/ToolPart';
import { useCopilotToolUiOptional } from '@/features/copilot/ui/ToolUi/useCopilotToolUi';
import { priorAuthKeys } from '@/entities/prior-auth';

function parseRecordId(output: unknown): string | null {
  if (!output || typeof output !== 'object') return null;
  const recordId = (output as Record<string, unknown>).prior_auth_record_id;
  return typeof recordId === 'string' && recordId.length > 0 ? recordId : null;
}

/**
 * Renderer for the ``answer_prior_auth_question`` tool part.
 *
 * The agent answers PA questions server-side (straight to DoseSpot), which
 * the wizard never learns about on its own — its details query only refreshes
 * on a 30s poll or on clinician-driven mutations. This card contributes no
 * visible UI (the shared ``ToolActivityLine`` already shows progress); it
 * exists solely to bridge the tool's completion to React Query, invalidating
 * the open case's details so the pending-question form and answered transcript
 * update immediately.
 *
 * Mirrors the ``draft_prior_auth_letter`` → ``$lmnDraftStatus`` bridge pattern
 * in :func:`DraftPriorAuthLetterToolCard`.
 */
export function AnswerPriorAuthQuestionToolCard({ state, output }: ToolPartCustomRendererArgs) {
  const queryClient = useQueryClient();
  const toolUi = useCopilotToolUiOptional();
  const patientUserId = toolUi?.patientId;

  // Each answer submits one question and can reveal the next; the agent often
  // chains several ``answer_prior_auth_question`` calls in a single turn, each
  // its own tool part. Guard so a given completed part invalidates only once
  // (re-renders shouldn't re-fire), while still letting every distinct part in
  // the chain refresh the wizard as it lands.
  const invalidatedRef = useRef(false);

  useEffect(() => {
    if (state !== 'output-available') return;
    if (invalidatedRef.current) return;

    const recordId = parseRecordId(output);
    if (recordId === null) return;

    invalidatedRef.current = true;

    void queryClient.invalidateQueries({ queryKey: priorAuthKeys.details(recordId) });
    if (patientUserId) {
      void queryClient.invalidateQueries({
        queryKey: priorAuthKeys.patientList(patientUserId),
      });
    }
  }, [state, output, patientUserId, queryClient]);

  return null;
}
